"use client"

import { useRouter } from "next/navigation"
import { Suspense, use, useRef } from "react"

import type { AnimationEvent, ReactNode, RefObject } from "react"

import { Dialog, DialogOverlay, DialogPortal } from "@/components/ui/dialog"
import { useMountEffect } from "@/hooks/useMountEffect"

import { CenterSpinner } from "./CenterSpinner"
import { ModalInstanceContext } from "./instance-context"
import { ModalLoadBoundary } from "./ModalLoadBoundary"
import { ModalSurface } from "./ModalSurface"
import {
  invalidateModal,
  loadModal,
  type ModalName,
  type ModalProps,
  type ModalShellConfig
} from "./registry"
import { useSwipeDownClose } from "./useSwipeDownClose"

// Fallback when the exit animation never runs: prefers-reduced-motion, or the
// modal was closed while its chunk was still loading, so no content ever
// mounted to fire onAnimationEnd. remove() is idempotent, a double call is safe.
function ExitFallback({ onExited }: { onExited?: () => void }) {
  useMountEffect(() => {
    const timeoutId = window.setTimeout(() => onExited?.(), 300)
    return () => window.clearTimeout(timeoutId)
  })

  return null
}

// Must stay inside Suspense and under ModalLoadBoundary: the surface itself
// comes from the feature chunk, so the spinner and the retry prompt are the
// only chrome the user sees until use() resolves.
function RegistryModal<N extends ModalName>({
  name,
  props,
  contentRef,
  onAnimationEnd
}: {
  name: N
  props: ModalProps[N]
  contentRef: RefObject<HTMLDivElement | null>
  onAnimationEnd: (event: AnimationEvent<HTMLDivElement>) => void
}) {
  const { default: Content, shell } = use(loadModal(name))

  return (
    <ModalSurface
      ref={contentRef}
      className={shell?.className}
      onAnimationEnd={onAnimationEnd}
    >
      <Content {...props} />
    </ModalSurface>
  )
}

type ModalShellBaseProps = {
  open?: boolean
  onClose?: (result?: unknown) => void
  onExited?: () => void
  // Intercepting routes only: the registry branch reads the same declaration
  // from the loaded module instead.
  shell?: ModalShellConfig
}

// Either the caller renders the content itself (intercepting routes), or the
// shell loads it from the registry and owns the loading phase.
type ModalShellProps = ModalShellBaseProps &
  (
    | { children: ReactNode; name?: undefined; props?: undefined }
    | { children?: undefined; name: ModalName; props: ModalProps[ModalName] }
  )

export function ModalShell(shellProps: ModalShellProps) {
  const { open = true, onClose, onExited, shell } = shellProps
  const router = useRouter()
  const close = onClose ?? (() => router.back())
  const contentRef = useRef<HTMLDivElement>(null)
  const swipe = useSwipeDownClose(contentRef, close)

  const handleAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return
    if (!open && event.currentTarget.dataset.state === "closed") onExited?.()
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && close()}>
      <ModalInstanceContext.Provider value={{ close, swipe }}>
        {/* Overlay is mounted outside Suspense so the backdrop does not
            re-fade when the content replaces the spinner. */}
        <DialogPortal>
          <DialogOverlay
            className={
              shellProps.name === "onboarding"
                ? "max-md:top-18 max-md:right-0 max-md:bottom-0 max-md:left-0"
                : undefined
            }
          />
        </DialogPortal>
        {shellProps.name === undefined ? (
          <ModalSurface
            ref={contentRef}
            className={shell?.className}
            onAnimationEnd={handleAnimationEnd}
          >
            {shellProps.children}
          </ModalSurface>
        ) : (
          <ModalLoadBoundary
            onRetry={() => invalidateModal(shellProps.name)}
            onClose={close}
          >
            <Suspense
              fallback={
                <DialogPortal>
                  <CenterSpinner />
                </DialogPortal>
              }
            >
              <RegistryModal
                name={shellProps.name}
                props={shellProps.props}
                contentRef={contentRef}
                onAnimationEnd={handleAnimationEnd}
              />
            </Suspense>
          </ModalLoadBoundary>
        )}
      </ModalInstanceContext.Provider>
      {!open && <ExitFallback onExited={onExited} />}
    </Dialog>
  )
}
